import { Component } from '@theme/component';

/**
 * DAVIDsTEA marquee: a strip of copy that loops sideways forever.
 *
 * The section renders the run once. This lays that run out into a rail that loops without ever
 * showing bare background, and it is built so that a measurement going out of date cannot put
 * a hole in the strip — which is what the previous shape did, repeatedly, on a phone.
 *
 * The rail is two identical groups, and the animation slides the track by exactly `-50%`:
 *
 *   track ─┬─ group        the original run, plus enough copies to cover the viewport and one over
 *          └─ group copy   the same again, aria-hidden
 *
 * A percentage translation resolves against the track's own width at paint time, so half a
 * track is one group whatever a group currently measures, and the second group always lands
 * exactly where the first started. That is the whole point of the shape. The strip is set in
 * Alternate Gothic with `font-display: swap`, and a condensed face is far narrower than the
 * system fallback that stands in for it, so the run shrinks under the theme's own feet some
 * time after it is first measured. The animation used to slide by a measured pixel width, so a
 * run that had since shrunk ran out before the animation did and the tail of every cycle was
 * bare background. Measured here: with the re-measure suppressed and the copy narrowed the way
 * the swap narrows it, the old rail scrolled up to 70px of empty band through a 390px viewport.
 * Nothing is measured in the animation any more, so there is nothing left to go stale.
 *
 * What is still measured is how many copies a group needs, and that one is deliberately
 * generous: enough to cover the viewport, plus one. A copy too many costs a few nodes; a copy
 * too few is the bug again on the next font swap.
 *
 * The observer watches the run itself rather than only the host. The host is full width and
 * its size does not change when a font swaps in, so it never reported any of this.
 *
 * Reduced motion is answered here rather than in CSS. A stopped rail is worse than the motion
 * was — a `max-content` row inside a viewport that clips is a line of copy cut off mid-word
 * with no way to reach the rest — so the rail is simply never built: no copies, no groups, no
 * `data-ready`, and the section's stylesheet is left with one run it can wrap and centre.
 *
 * Shared with sections/dt-donation-marquee.liquid, so it stays class-agnostic: it works with
 * whatever carries `ref="track"` and `ref="run"` and builds the groups itself.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track
 * @property {HTMLElement} run
 *
 * @extends {Component<Refs>}
 */
export class DtMarquee extends Component {
  requiredRefs = ['track', 'run'];

  /** @type {ResizeObserver | undefined} */
  #observer;

  /** @type {MediaQueryList | undefined} */
  #reducedMotion;

  /** Guards against re-entering the layout from the observer it mutates the track under. */
  #laying = false;

  /** The measurements the current rail was built from, to skip work that would change nothing. */
  #lastRunWidth = 0;
  #lastHostWidth = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion.addEventListener('change', this.#onMotionPreferenceChange);

    this.#layout();

    this.#observer = new ResizeObserver(() => this.#layout());
    this.#observer.observe(this);
    this.#observer.observe(this.refs.run);

    /*
     * The observer catches the swap on its own in every browser that reports the run's new
     * width, and this is the belt to that braces: `fonts.ready` also covers the case where the
     * face lands between the first measurement and the observer being wired up.
     */
    document.fonts?.ready.then(() => this.#layout());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#reducedMotion?.removeEventListener('change', this.#onMotionPreferenceChange);
  }

  #onMotionPreferenceChange = () => {
    // The measurements the rail was built from have not changed, so clear them or #layout
    // would decide there is nothing to do and leave the rail as it is.
    this.#lastRunWidth = 0;
    this.#lastHostWidth = 0;
    this.#layout();
  };

  /**
   * Returns the track to the single run the section rendered, dropping every copy and group
   * wrapper a previous pass put down. Called before each rebuild so repeated passes cannot
   * accumulate, and on its own when the rail should not exist at all.
   */
  #reset() {
    const { track, run } = this.refs;

    // The run itself is moved back to the top level first: it is inside a group by now, and
    // removing the groups with it still inside would take the section's own markup with them.
    track.append(run);
    for (const stale of track.querySelectorAll('[data-clone], [data-marquee-group]')) stale.remove();
  }

  #layout() {
    if (this.#laying) return;

    this.#laying = true;
    try {
      if (this.#reducedMotion?.matches) {
        this.#reset();
        this.removeAttribute('data-ready');
        this.style.removeProperty('--dt-marquee-duration');
        this.#lastRunWidth = 0;
        this.#lastHostWidth = 0;
        return;
      }

      const { track, run } = this.refs;
      const runWidth = run.getBoundingClientRect().width;
      const hostWidth = this.getBoundingClientRect().width;
      if (!runWidth || !hostWidth) return;

      // Sub-pixel churn — a reflow that lands a hundredth of a pixel away — would otherwise
      // rebuild the rail on every frame of a resize.
      if (Math.abs(runWidth - this.#lastRunWidth) < 0.5 && Math.abs(hostWidth - this.#lastHostWidth) < 0.5) {
        return;
      }

      this.#reset();

      // Cover the viewport, and one run over. See the note above on why this is generous.
      const copies = Math.ceil(hostWidth / runWidth) + 1;

      const group = document.createElement('div');
      group.setAttribute('data-marquee-group', '');
      group.style.display = 'flex';
      group.style.flex = 'none';
      group.append(run);

      for (let i = 1; i < copies; i++) {
        const copy = /** @type {HTMLElement} */ (run.cloneNode(true));
        // Copies are decorative repeats of copy the first run already announced.
        copy.setAttribute('aria-hidden', 'true');
        copy.setAttribute('data-clone', '');
        copy.removeAttribute('ref');
        group.append(copy);
      }

      const second = /** @type {HTMLElement} */ (group.cloneNode(true));
      second.setAttribute('aria-hidden', 'true');
      second.setAttribute('data-clone', '');
      // The run's own ref rode along in the clone; the component must keep pointing at the one
      // the section rendered.
      second.querySelector('[ref]')?.removeAttribute('ref');

      track.append(group, second);

      // Only the cycle length is taken from the measurement now, and a stale one there costs a
      // slightly wrong speed rather than a hole in the strip.
      const speed = Number(this.dataset.speed) || 60;
      this.style.setProperty('--dt-marquee-duration', `${(runWidth * copies) / speed}s`);
      this.setAttribute('data-ready', '');

      this.#lastRunWidth = runWidth;
      this.#lastHostWidth = hostWidth;
    } finally {
      this.#laying = false;
    }
  }
}

if (!customElements.get('dt-marquee-component')) {
  customElements.define('dt-marquee-component', DtMarquee);
}
